import * as React from "react";
import { Flex, Box, Text, Spinner } from "@radix-ui/themes";
import { CheckCircleIcon, FileIcon } from "@phosphor-icons/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { v0_9 } from "@a2ui-sdk/react";
import type { A2UIMessage, ActionPayload as A2UIAction } from "@a2ui-sdk/types/0.9";
import { sharedMarkdownComponents, formatMarkdownText } from "./MarkdownRenderer";
import type { Message, DraftEmail } from "../../../types";
import type { Content, FunctionCall } from "@google/genai";
import { injectDraftEmail } from "../../../lib/outlook/emailData";

const { A2UIProvider, A2UIRenderer } = v0_9;

interface ChatMessageProps {
  msg: Message;
  customCatalog: NonNullable<React.ComponentProps<typeof A2UIProvider>['catalog']>;
  onSendAction?: (actionPayload: Content) => void;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({ msg, customCatalog, onSendAction }) => {
  const [localA2UIMessages, setLocalA2UIMessages] = React.useState<A2UIMessage[]>([]);

  const a2uiMessages = msg.a2uiMessages || [];
  const hasA2UI = a2uiMessages.length > 0;
  const textToRender = msg.displayContent !== undefined ? msg.displayContent : msg.content;

  const allA2UIMessages = React.useMemo(() => {
    return [...(a2uiMessages as unknown as A2UIMessage[]), ...localA2UIMessages];
  }, [a2uiMessages, localA2UIMessages]);

  const handleA2UIAction = React.useCallback((action: A2UIAction) => {
    if (action.name === "inject_draft" && action.context?.draft) {
      injectDraftEmail(action.context.draft as DraftEmail).catch(err => console.error("Error injecting draft via action:", err));
    } else if (action.name === "confirmFunctionCall" && onSendAction) {
      const functionCall = action.context?.functionCall as FunctionCall | undefined;
      const toolConfirmationConfirmed = action.context?.toolConfirmationConfirmed as boolean | undefined;
      
      if (!functionCall) return;

      const args = functionCall.args as Record<string, Record<string, unknown>> | undefined;
      const originalPayload = args?.toolConfirmation?.payload || {};
      const payload = action.context?.payload || originalPayload;

      // Optimistic UI update via A2UI Data Model
      setLocalA2UIMessages(prev => [
        ...prev,
        {
          updateDataModel: {
            surfaceId: `tool-confirmation-${functionCall.id}`,
            value: {
              isConfirmed: true,
              confirmText: "✓ Confirmed",
              isPrimary: false,
              payload
            }
          }
        } as A2UIMessage
      ]);

      onSendAction({ 
        role: "user",
        parts: [{
          functionResponse: {
            id: functionCall.id || "",
            name: functionCall.name || "",
            response: {
              confirmed: Boolean(toolConfirmationConfirmed),
              payload
            }
          }
        }]
      });
    }
  }, [onSendAction]);

  if (
    msg.role === "assistant" &&
    !textToRender &&
    !msg.status &&
    (!msg.steps || msg.steps.length === 0) &&
    !msg.isStreaming &&
    !hasA2UI &&
    !msg.isBufferingA2ui
  ) {
    return null;
  }

  return (
    <Flex justify={msg.role === "user" ? "end" : "start"} style={{ width: '100%' }}>
      <Flex
        direction="column"
        gap="1"
        className={`chat-message-item ${msg.role === "assistant" ? "chat-message-item-assistant" : ""}`}
      >
        <Box
          className={`chat-message-bubble ${
            msg.role === "user" ? "chat-message-user" : "chat-message-assistant"
          }`}
        >
          {msg.steps && msg.steps.length > 0 && (
            <Flex direction="column" gap="1" mb={textToRender ? "2" : "0"}>
              {msg.steps.map((step) => (
                <Flex key={step.id} align="center" gap="2">
                  {step.done ? (
                    <CheckCircleIcon weight="fill" color="var(--accent-9)" size={14} />
                  ) : (
                    <Spinner size="1" />
                  )}
                  <Text size="1" color="gray" style={{ fontStyle: "italic" }}>
                    {step.text}
                  </Text>
                </Flex>
              ))}
            </Flex>
          )}
          {(!msg.steps || msg.steps.length === 0) && msg.status && (
            <Flex align="center" gap="2" mb={textToRender ? "2" : "0"}>
              <Spinner size="1" />
              <Text size="1" color="gray" style={{ fontStyle: "italic" }}>
                {msg.status}
              </Text>
            </Flex>
          )}
          {textToRender && (
            <Box className="markdown-content">
              <ReactMarkdown
                components={sharedMarkdownComponents}
                remarkPlugins={[remarkGfm]}
              >
                {formatMarkdownText(textToRender)}
              </ReactMarkdown>
            </Box>
          )}
          {hasA2UI && (
            <Box mt={textToRender || msg.status || (msg.steps && msg.steps.length > 0) ? "3" : "0"}>
              <A2UIProvider messages={allA2UIMessages} catalog={customCatalog}>
                {Array.from(
                  new Set(
                    allA2UIMessages.map(
                      (m) => {
                        if ('updateComponents' in m) return m.updateComponents.surfaceId;
                        if ('createSurface' in m) return m.createSurface.surfaceId;
                        if ('surfaceId' in m) return m.surfaceId as string;
                        return "main-chat";
                      }
                    )
                  )
                ).map((sid) => (
                  <A2UIRenderer key={sid as string} surfaceId={sid as string} onAction={handleA2UIAction} />
                ))}
              </A2UIProvider>
            </Box>
          )}
          {msg.isBufferingA2ui && (
            <Flex align="center" gap="2" mt={textToRender || msg.status || (msg.steps && msg.steps.length > 0) ? "2" : "0"}>
              <Spinner size="1" />
              <Text size="1" color="gray" style={{ fontStyle: "italic" }}>
                Generating UI components...
              </Text>
            </Flex>
          )}
          {!textToRender &&
            !msg.status &&
            (!msg.steps || msg.steps.length === 0) &&
            msg.isStreaming &&
            !hasA2UI &&
            !msg.isBufferingA2ui && (
              <Flex align="center" gap="2">
                <Spinner size="1" />
                <Text size="1" color="gray" style={{ fontStyle: "italic" }}>
                  Thinking...
                </Text>
              </Flex>
            )}
        </Box>
        {msg.files && msg.files.length > 0 && (
          <Flex
            gap="2"
            wrap="wrap"
            mt="1"
            justify={msg.role === "user" ? "end" : "start"}
          >
            {msg.files.map((f, i) => (
              <Flex key={i} align="center" gap="1" className="chat-attached-file">
                <FileIcon size={12} />
                <Text size="1" color="gray" className="chat-attached-file-name">
                  {f.name}
                </Text>
              </Flex>
            ))}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};
